/**
 * PersistentGhost - Keeps Ghost Admin iframe mounted across navigation
 *
 * Mirrors PersistentCrm pattern:
 * - Mounted when user first visits /blog (lazy initialization)
 * - Kept mounted when navigating away (hidden with CSS)
 * - Shown instantly when returning to /blog
 *
 * URL sync: two-way browser URL <-> iframe path synchronization
 * - Iframe GHOST_ROUTE_CHANGE -> updates browser URL (push for significant, replace for minor)
 * - Browser back/forward -> sends GHOST_NAVIGATE to iframe
 * - Loop prevention via parentInitiated flag + lastSyncedPath comparison
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { GhostEmbed } from "@/components/GhostEmbed";
import type { GhostEmbedRef } from "@/components/GhostEmbed/types";
import { useAuthStore } from "@/lib/store/authStore";
import { isSignificantNavigation } from "./urlSyncUtils";

export function PersistentGhost() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnBlogPage = location.pathname.startsWith("/blog");

  // Lazy mount: only render after first visit
  const [hasVisitedBlog, setHasVisitedBlog] = useState(false);
  const [mountKey, setMountKey] = useState(0);

  // URL sync refs — loop prevention + deep link initialPath
  const lastSyncedPath = useRef("");
  const initialPathRef = useRef<string | undefined>(undefined);
  const parentInitiated = useRef(false);
  const ghostRef = useRef<GhostEmbedRef>(null);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  // Reset on logout
  const wasAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      setHasVisitedBlog(false);
      setMountKey((prev) => prev + 1);
      lastSyncedPath.current = "";
      initialPathRef.current = undefined;
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Lazy mount on first /blog visit — extract sub-path for deep linking
  useEffect(() => {
    if (isOnBlogPage && !hasVisitedBlog) {
      // Extract sub-path: "/blog/editor/post/abc" -> "/editor/post/abc", "/blog" -> ""
      const subPath = location.pathname.slice(5); // Remove "/blog"
      // Ghost Admin uses hash-based routes (#/editor/post/abc123)
      // If sub-path provided, convert to hash: "/editor/post/abc" -> "#/editor/post/abc"
      initialPathRef.current = subPath ? `#${subPath}` : undefined;
      lastSyncedPath.current = subPath;
      setHasVisitedBlog(true);
    }
  }, [isOnBlogPage, hasVisitedBlog, location.pathname]);

  // Restore last-viewed URL when returning to blog from another app (sidebar click)
  useEffect(() => {
    if (parentInitiated.current) return;
    if (isOnBlogPage && hasVisitedBlog && lastSyncedPath.current) {
      const expectedPath = `/blog${lastSyncedPath.current}`;
      if (location.pathname !== expectedPath) {
        navigate(expectedPath, { replace: true });
      }
    }
  }, [isOnBlogPage, hasVisitedBlog, navigate, location.pathname]);

  // Back/forward button: when location changes while on blog page,
  // send GHOST_NAVIGATE to iframe if path differs from lastSyncedPath
  useEffect(() => {
    if (!isOnBlogPage || !hasVisitedBlog) return;
    const subPath = location.pathname.slice(5); // Remove "/blog"
    if (subPath && subPath !== lastSyncedPath.current) {
      if (!ghostRef.current?.isReady) return;
      parentInitiated.current = true;
      lastSyncedPath.current = subPath;
      // Convert dashboard path to Ghost hash route
      ghostRef.current?.navigate(`#${subPath}`);
      // Safety: reset parentInitiated if no ROUTE_CHANGE echo within 500ms
      const safetyTimer = setTimeout(() => {
        parentInitiated.current = false;
      }, 500);
      return () => clearTimeout(safetyTimer);
    }
  }, [location.pathname, isOnBlogPage, hasVisitedBlog]);

  const handleReady = useCallback(() => {
    // Ghost Admin loaded
  }, []);

  const handleRouteChange = useCallback(
    (path: string, _title: string) => {
      // Ghost sends hash-based paths like "#/editor/post/abc123"
      // Convert to dashboard path: strip leading "#" -> "/editor/post/abc123"
      if (parentInitiated.current) {
        parentInitiated.current = false;
        return; // Skip — we initiated this navigation via back/forward
      }

      // Normalize: strip leading "#" or "#/" prefix for comparison
      const normalizedPath = path.startsWith("#") ? path.slice(1) : path;
      if (lastSyncedPath.current === normalizedPath) return; // Skip duplicate

      const prevPath = lastSyncedPath.current;
      lastSyncedPath.current = normalizedPath;

      if (isOnBlogPage) {
        const shouldPush = isSignificantNavigation(prevPath, normalizedPath);
        navigate(`/blog${normalizedPath}`, { replace: !shouldPush });
      }
    },
    [navigate, isOnBlogPage]
  );

  const handleError = useCallback((message: string) => {
    console.error("[PersistentGhost] Error:", message);
  }, []);

  // Don't render if never visited or not provisioned
  if (!hasVisitedBlog || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnBlogPage ? "visible" : "hidden",
        pointerEvents: isOnBlogPage ? "auto" : "none",
      }}
    >
      <GhostEmbed
        ref={ghostRef}
        key={mountKey}
        initialPath={initialPathRef.current}
        className="h-full w-full"
        onReady={handleReady}
        onRouteChange={handleRouteChange}
        onError={handleError}
      />
    </div>
  );
}
